import {Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnDestroy} from '@angular/core';
import {Subject, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {WebcamImage, WebcamInitError, WebcamUtil} from 'ngx-webcam';
import {DeviceDetectorService} from 'ngx-device-detector';
import {selectBestCamera} from '../../_helpers/camera-selector.support';
import {WebOnboardingService} from '../../_services/web-onboarding.service';

@Component({
  selector: 'app-dni-back',
  templateUrl: './dni-back.component.html',
  styleUrls: ['./dni-back.component.css'],
})
export class DniBackComponent implements OnDestroy {
  @Input() frontFile!: File;
  @Output() validated = new EventEmitter<any>();
  @Output() validationFailed = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  showWebcam = true;
  cameraReady = false;
  cameraError = false;
  cameraErrorMessage = '';
  capturedImage: WebcamImage | null = null;
  loading = false;
  errorMessage = '';
  facingMode = 'environment';

  private readonly mobileAspectRatio = 1080 / 1920;
  private readonly desktopAspectRatio = 1920 / 1080;

  private trigger$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private nextWebcam$ = new Subject<boolean | string>();

  get triggerObservable(): Observable<void> {
    return this.trigger$.asObservable();
  }

  get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam$.asObservable();
  }

  get videoOptions(): MediaTrackConstraints {
    const result: MediaTrackConstraints = {};
    if (this.facingMode) {
      result.facingMode = {ideal: this.facingMode};
    }
    if (this.deviceService.isMobile()) {
      result.aspectRatio = this.mobileAspectRatio;
    } else {
      result.aspectRatio = this.desktopAspectRatio;
    }
    return result;
  }

  constructor(private onboardingService: WebOnboardingService, private deviceService: DeviceDetectorService) {}

  ngOnInit(): void {
    WebcamUtil.getAvailableVideoInputs().then((devices) => {
      if (devices && devices.length > 0) {
        this.cameraReady = true;
        selectBestCamera(devices, this.nextWebcam$);
      } else {
        this.cameraError = true;
        this.cameraErrorMessage = 'No se detectó ninguna cámara. Puede subir una foto desde la galería.';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCameraInit(error: WebcamInitError): void {
    this.cameraError = true;
    this.cameraErrorMessage = 'No se pudo acceder a la cámara. Puede subir una foto desde la galería.';
  }

  capture(): void {
    this.trigger$.next();
  }

  onImageCaptured(image: WebcamImage): void {
    this.capturedImage = image;
    this.showWebcam = false;
  }

  retake(): void {
    this.capturedImage = null;
    this.showWebcam = true;
    this.errorMessage = '';
  }

  confirmCapture(): void {
    if (this.capturedImage) {
      this.padImageForIS(this.capturedImage.imageAsDataUrl, 'dni-dorso.jpg').then((file) => {
        this.uploadDni(file);
      });
    }
  }

  openGallery(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const reader = new FileReader();
      reader.onload = () => {
        this.padImageForIS(reader.result as string, 'dni-dorso.jpg').then((file) => {
          this.uploadDni(file);
        });
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  private padImageForIS(dataUrl: string, filename: string): Promise<File> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const targetWidth = 1080;
        const scale = targetWidth / img.width;
        const scaledHeight = img.height * scale;
        const targetHeight = scaledHeight * 3;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        const y = (targetHeight - scaledHeight) / 2;
        ctx.drawImage(img, 0, y, targetWidth, scaledHeight);

        canvas.toBlob((blob) => {
          resolve(new File([blob!], filename, {type: 'image/jpeg'}));
        }, 'image/jpeg', 0.9);
      };
      img.src = dataUrl;
    });
  }

  private uploadDni(backFile: File): void {
    this.loading = true;
    this.errorMessage = '';

    this.onboardingService
      .uploadDni(this.frontFile, backFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (response.errors?.length || response.success === false) {
            const msg = response.errors?.length
              ? response.errors.map((e: any) => e.message).filter(Boolean).join('\n')
              : (response.message || 'El documento no ha sido validado. Intentá nuevamente.');
            alert(msg);
            this.validationFailed.emit();
            return;
          }
          this.validated.emit(response);
        },
        error: (err) => {
          this.loading = false;
          const body = err.error;
          let msg: string;
          if (body?.errors?.length) {
            msg = body.errors.map((e: any) => e.message).filter(Boolean).join('\n');
          } else if (body?.message) {
            msg = body.message;
          } else {
            msg = 'Error al procesar el DNI. Intentá nuevamente.';
          }
          alert(msg);
          this.validationFailed.emit();
        },
      });
  }

  private dataUrlToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type: mime});
  }
}
